import { resolveWindRadiiAtTime, type WindRadii as TimedWindRadii } from "@shared/domain";
import type { StormPoint, WindRadii } from "../domain/types";

const earthRadiusKm = 6371.0088;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
const toDegrees = (radians: number) => (radians * 180) / Math.PI;

const beaufortUpperBoundsKph = [1, 5, 11, 19, 28, 38, 49, 61, 74, 88, 102, 117, 133, 149, 166, 183, 201];

export function resolvePublicAssetUrl(filename: string, baseUrl = import.meta.env.BASE_URL): string {
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
  return `${normalizedBase}assets/${filename.replace(/^\/+/, "")}`;
}

export function windSpeedKphToBeaufort(windSpeedKph: number | null): number | null {
  if (windSpeedKph === null || !Number.isFinite(windSpeedKph) || windSpeedKph < 0) return null;
  const scale = beaufortUpperBoundsKph.findIndex((upperBound) => windSpeedKph <= upperBound);
  return scale === -1 ? 17 : scale;
}

export function classifyTyphoonByWindSpeed(windSpeedKph: number | null): string {
  if (windSpeedKph === null || !Number.isFinite(windSpeedKph) || windSpeedKph < 0) return "等级未知";
  if (windSpeedKph < 17.2 * 3.6) return "热带低压";
  if (windSpeedKph < 24.5 * 3.6) return "热带风暴";
  if (windSpeedKph < 32.7 * 3.6) return "强热带风暴";
  if (windSpeedKph < 41.5 * 3.6) return "台风";
  if (windSpeedKph < 51 * 3.6) return "强台风";
  return "超强台风";
}

function sphericalDistanceKm(
  start: Pick<StormPoint, "latitude" | "longitude">,
  end: Pick<StormPoint, "latitude" | "longitude">,
) {
  const startLat = toRadians(start.latitude);
  const endLat = toRadians(end.latitude);
  const latitudeDelta = endLat - startLat;
  const longitudeDelta = toRadians(end.longitude - start.longitude);
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(startLat) * Math.cos(endLat) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.min(1, Math.sqrt(haversine)));
}

export function calculateMovementSpeedKph(points: StormPoint[], targetTime: string): number | null {
  const ordered = [...points]
    .filter((point) => Number.isFinite(Date.parse(point.time)))
    .sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
  if (ordered.length < 2) return null;

  const target = Date.parse(targetTime);
  let endIndex = ordered.findIndex((point) => Date.parse(point.time) >= target);
  if (endIndex < 0) endIndex = ordered.length - 1;
  if (endIndex === 0) endIndex = 1;

  const start = ordered[endIndex - 1];
  const end = ordered[endIndex];
  if (!start || !end) return null;
  const elapsedHours = (Date.parse(end.time) - Date.parse(start.time)) / 3_600_000;
  if (!Number.isFinite(elapsedHours) || elapsedHours <= 0) return null;
  return sphericalDistanceKm(start, end) / elapsedHours;
}

function interpolateNullable(a: number | null, b: number | null, ratio: number) {
  if (a === null && b === null) return null;
  if (a === null) return b;
  if (b === null) return a;
  return a + (b - a) * ratio;
}

function sphericalInterpolate(
  start: Pick<StormPoint, "latitude" | "longitude">,
  end: Pick<StormPoint, "latitude" | "longitude">,
  ratio: number,
) {
  const startLat = toRadians(start.latitude);
  const startLng = toRadians(start.longitude);
  const endLat = toRadians(end.latitude);
  const endLng = toRadians(end.longitude);
  const angularDistance = 2 * Math.asin(Math.sqrt(
    Math.sin((endLat - startLat) / 2) ** 2
      + Math.cos(startLat) * Math.cos(endLat) * Math.sin((endLng - startLng) / 2) ** 2,
  ));
  if (angularDistance < 1e-10) return { latitude: start.latitude, longitude: start.longitude };
  const a = Math.sin((1 - ratio) * angularDistance) / Math.sin(angularDistance);
  const b = Math.sin(ratio * angularDistance) / Math.sin(angularDistance);
  const x = a * Math.cos(startLat) * Math.cos(startLng) + b * Math.cos(endLat) * Math.cos(endLng);
  const y = a * Math.cos(startLat) * Math.sin(startLng) + b * Math.cos(endLat) * Math.sin(endLng);
  const z = a * Math.sin(startLat) + b * Math.sin(endLat);
  return {
    latitude: toDegrees(Math.atan2(z, Math.sqrt(x * x + y * y))),
    longitude: toDegrees(Math.atan2(y, x)),
  };
}

function normalizeTimedRadii(radii: TimedWindRadii): WindRadii[] {
  return radii.rings.map((ring) => ({
    windScale: ring.thresholdKt === 30 ? 7 : ring.thresholdKt === 50 ? 10 : 12,
    thresholdKt: ring.thresholdKt,
    quadrantsKm: ring.quadrantsKm,
    evidence: radii.evidence,
  }));
}

function buildRadiiTimeline(points: StormPoint[]): TimedWindRadii[] {
  return points.flatMap((point): TimedWindRadii[] => {
    const firstRadius = point.windRadii[0];
    if (!firstRadius) return [];
    return [{
      validTime: new Date(point.time).toISOString(),
      evidence: firstRadius.evidence,
      rings: point.windRadii.map((radius) => ({
        thresholdKt: radius.thresholdKt,
        quadrantsKm: radius.quadrantsKm,
      })),
    }];
  });
}

function attachTimelineRadii(
  point: StormPoint,
  timeline: TimedWindRadii[] | undefined,
): StormPoint {
  if (!timeline?.length) return point;
  const resolved = resolveWindRadiiAtTime(timeline, point.time);
  return resolved ? { ...point, windRadii: normalizeTimedRadii(resolved) } : point;
}

export function interpolateStormPoint(
  points: StormPoint[],
  targetTime: string,
  windRadiiTimeline?: TimedWindRadii[],
): StormPoint | null {
  if (points.length === 0) return null;
  const ordered = [...points].sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
  const radiiTimeline = windRadiiTimeline?.length
    ? windRadiiTimeline
    : buildRadiiTimeline(ordered);
  const target = Date.parse(targetTime);
  const first = ordered[0];
  const last = ordered.at(-1);
  if (!first || !last || !Number.isFinite(target)) {
    return first ? attachTimelineRadii(first, radiiTimeline) : null;
  }
  if (target <= Date.parse(first.time)) return attachTimelineRadii(first, radiiTimeline);
  if (target >= Date.parse(last.time)) return attachTimelineRadii(last, radiiTimeline);

  for (let index = 0; index < ordered.length - 1; index += 1) {
    const start = ordered[index];
    const end = ordered[index + 1];
    if (!start || !end) continue;
    const startMs = Date.parse(start.time);
    const endMs = Date.parse(end.time);
    if (target < startMs || target > endMs) continue;
    if (target === startMs) return attachTimelineRadii(start, radiiTimeline);
    if (target === endMs) return attachTimelineRadii(end, radiiTimeline);
    const ratio = (target - startMs) / (endMs - startMs);
    const position = sphericalInterpolate(start, end, ratio);
    return attachTimelineRadii({
      id: `${start.id}:${end.id}:${ratio.toFixed(4)}`,
      time: targetTime,
      ...position,
      windSpeedKph: interpolateNullable(start.windSpeedKph, end.windSpeedKph, ratio),
      pressureHpa: interpolateNullable(start.pressureHpa, end.pressureHpa, ratio),
      intensity: ratio < 0.5 ? start.intensity : end.intensity,
      evidence: end.evidence,
      windRadii: [],
    }, radiiTimeline);
  }
  return attachTimelineRadii(last, radiiTimeline);
}

export function resolveLiveTimelineStart(points: StormPoint[], currentTime: string): string {
  const ordered = [...points].sort((a, b) => Date.parse(a.time) - Date.parse(b.time));
  const first = ordered[0];
  const last = ordered.at(-1);
  if (!first || !last) return currentTime;
  const now = Date.parse(currentTime);
  if (!Number.isFinite(now) || now <= Date.parse(first.time)) return first.time;
  if (now >= Date.parse(last.time)) return last.time;
  return new Date(Math.floor(now / 60_000) * 60_000).toISOString();
}

export interface RadiusPoint {
  latitude: number;
  longitude: number;
  radiusKm: number;
}

function destination(latitude: number, longitude: number, radiusKm: number, bearingDegrees: number): RadiusPoint {
  const angularDistance = radiusKm / earthRadiusKm;
  const bearing = toRadians(bearingDegrees);
  const lat = toRadians(latitude);
  const lng = toRadians(longitude);
  const destinationLat = Math.asin(
    Math.sin(lat) * Math.cos(angularDistance)
      + Math.cos(lat) * Math.sin(angularDistance) * Math.cos(bearing),
  );
  const destinationLng = lng + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(lat),
    Math.cos(angularDistance) - Math.sin(lat) * Math.sin(destinationLat),
  );
  return {
    latitude: toDegrees(destinationLat),
    longitude: ((toDegrees(destinationLng) + 540) % 360) - 180,
    radiusKm,
  };
}

export function buildQuadrantWindPolygon(
  latitude: number,
  longitude: number,
  radii: WindRadii,
  pointsPerQuadrant = 12,
): RadiusPoint[] {
  const quadrants = [
    { start: 0, radius: radii.quadrantsKm.northeast },
    { start: 90, radius: radii.quadrantsKm.southeast },
    { start: 180, radius: radii.quadrantsKm.southwest },
    { start: 270, radius: radii.quadrantsKm.northwest },
  ];
  const points = quadrants.flatMap(({ start, radius }) =>
    Array.from({ length: pointsPerQuadrant }, (_, index) =>
      destination(latitude, longitude, radius, start + (index * 90) / pointsPerQuadrant),
    ),
  );
  const first = points[0];
  return first ? [...points, first] : [];
}
